import React from "react";
import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { startCase, lowerCase, size, truncate } from "lodash";
import { FiEdit3 } from "react-icons/fi";
import { connect, type ConnectedProps } from "react-redux";
import { Form, Card } from "react-bootstrap";
import { capitalCase } from "change-case";

import IconSpinner from "components/IconSpinner";
import { getEntityLimits, updateEntityLimit } from "actions/adminActions";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import IconTableHeader from "components/IconTableHeader";
import SimpleModal from "components/SimpleModal";
import { createLoadingSelector } from "store/selectors";
import type { RootState } from "types/state";

import "./EntityLimits.scss";
import { adminConstants } from "../../../actions/types";

const loadingSelector = createLoadingSelector([
  adminConstants.GET_ENTITY_LIMITS,
]);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: loadingSelector(state),
  };
};

const mapDispatchToProps = {
  getEntityLimits,
  updateEntityLimit,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // `push` is declared in the original PropTypes but never passed by any
  // caller nor read in this file; kept optional to preserve the public shape.
  push?: (path: string) => void;
  entityType: "EMPLOYER" | "USER" | "SYSTEM";
  entityId?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

/** A limit row as fetched, augmented by loadLimits with edit bookkeeping. */
interface EntityLimitRow {
  key?: string;
  value?: string | null;
  originalValue?: string | null;
  hasBeenModified?: boolean;
  limitKey?: string;
}

interface State {
  limits: EntityLimitRow[];
  showEditModal: boolean;
  editLimit: EntityLimitRow;
  // written by loadLimits but never read from state (isFetching is read from props)
  isFetching?: boolean;
  // written by submitLimitUpdate; read from state to drive the modal spinner
  isUpdating?: boolean;
}

class EntityLimits extends React.PureComponent<Props, State> {
  static defaultProps: Pick<OwnProps, "entityType"> = {
    entityType: "SYSTEM",
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      limits: [],
      showEditModal: false,
      editLimit: {},
    };
  }

  async componentDidMount() {
    this.loadLimits();
  }

  loadLimits = () => {
    void this.props
      .getEntityLimits(
        this.props.client as ApolloClient<object>,
        this.props.entityType,
        this.props.entityId
      )
      .then((action) => {
        // The thunk resolves to the dispatched action union
        // ({ type, data } success | { type, error } failure). The original
        // reads `data` (the fetched limits) off the success branch; cast to
        // surface it without changing the runtime read.
        const { data } = action as unknown as {
          data: { key: string; value: string | null }[];
        };
        return this.setState({
          isFetching: false,
          // found-not-fixed: GetEntityLimitsQuery.getEntityLimits is
          // `Array | null`; `.map` is called unconditionally, so a null
          // payload throws at runtime. Typed as a non-null array here to
          // preserve the original behavior verbatim.
          limits: data.map((limit) => ({
            ...limit,
            originalValue: limit.value,
            hasBeenModified: false,
            limitKey: limit.key,
          })),
        });
      });
  };

  _getTableMetadata = (): Array<IconTableColumn<EntityLimitRow>> => {
    return [
      {
        key: "key",
        label: "Limit",
        format: (value) => {
          return capitalCase(value as string);
        },
      },
      {
        key: "value",
        label: "Value",
        format: (value) => {
          return truncate(value as string, { length: 60, separator: "..." });
        },
      },
      {
        label: "Action",
        customComponent: (props: EntityLimitRow) => {
          return (
            <span
              key={props.limitKey}
              className="action-icon"
              onClick={() => {
                this.setState({
                  showEditModal: true,
                  editLimit: props,
                });
              }}
            >
              <FiEdit3 size={18} stroke="#96AAC4" fill="white" />
            </span>
          );
        },
      },
    ];
  };

  submitLimitUpdate = () => {
    this.setState({ isUpdating: true });
    void this.props
      .updateEntityLimit(this.props.client as ApolloClient<object>, {
        entityId: this.props.entityId,
        entityType: this.props.entityType,
        // editLimit is only populated from a selected row before the modal
        // opens, so limitKey/value are present at call time; the optional
        // state shape requires casting to the mutation's required strings.
        key: this.state.editLimit.limitKey as string,
        value: this.state.editLimit.value as string,
      })
      .then(() => {
        this.loadLimits();
      })
      .finally(() => {
        this.setState({
          isUpdating: false,
          showEditModal: false,
          editLimit: {},
        });
      });
  };

  render() {
    if (this.props.isFetching) {
      return <IconSpinner centered />;
    }

    return (
      <>
        <h1 className="page-title">
          {`${startCase(lowerCase(this.props.entityType))} Limits`}
        </h1>
        <SimpleModal
          show={this.state.showEditModal}
          onClose={() => this.setState({ showEditModal: false })}
          onSubmit={this.submitLimitUpdate}
          title="Update Limit"
          buttonText="Update"
          closeButtonText="Cancel"
          isXhrRequest={true}
          btnDisabled={false}
          isFetching={this.state.isUpdating}
        >
          <Form.Control
            autoFocus
            // editLimit.value is `string | null`; Form.Control's value prop
            // rejects null, so pass it through as a string to preserve the
            // original (a null renders as an empty controlled input anyway).
            value={this.state.editLimit.value as string}
            onChange={(event) => {
              this.setState({
                editLimit: {
                  ...this.state.editLimit,
                  value: event.target.value,
                },
              });
            }}
          />
        </SimpleModal>
        <Card>
          {" "}
          <IconTableHeader
            tableCount={size(this.state.limits)}
            tableHeader="Limits"
          />
          <IconTable
            columns={this._getTableMetadata()}
            data={this.state.limits}
          />
        </Card>
      </>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withApollo<Props>(EntityLimits));
